import { ref } from 'vue'
import { initializeApp } from 'firebase/app'
import { getAnalytics, setAnalyticsCollectionEnabled, type Analytics } from 'firebase/analytics'
import { timeZones as consentRegionTimeZones } from '@/../../shared/tracking_consent_regions.json'
import { buildEnv } from '@/url'

declare global {
  interface Window {
    // Defined by the Meta Pixel snippet in index.html; inserts fbevents.js once the page has loaded.
    __ISSEN_LOAD_META_PIXEL__?: () => void
    fbq?: (...args: unknown[]) => void
  }
}

// The Meta Pixel (index.html) and Firebase/Google Analytics are the web app's only
// non-essential trackers. Visitors whose browser time zone is in the EEA, the UK or
// Switzerland get them only after accepting the cookie banner; everyone else gets
// them right away unless they rejected them. Login and session cookies are essential
// and are not affected.
export const TRACKING_CONSENT_STORAGE_KEY = 'issen-tracking-consent'
export type TrackingConsent = 'granted' | 'denied'

const CONSENT_REGION_TIME_ZONES = new Set(consentRegionTimeZones)
const TRACKER_COOKIE_PATTERN = /^(_fbp|_fbc|_ga|_ga_[A-Za-z0-9]+|_gid)$/
const FIREBASE_CONFIG = {
  apiKey: "AIzaSyC3bPkkeqAvUb-xxVn-UtagypNm0JSL-as",
  authDomain: "issen-appl.firebaseapp.com",
  projectId: "issen-appl",
  storageBucket: "issen-appl.firebasestorage.app",
  messagingSenderId: "307971587661",
  appId: "1:307971587661:web:1d8ca2e4a46575a3b3e604",
  measurementId: "G-FXGWW6G15V"
}

export const trackingConsentBannerOpen = ref(false)
let analytics: Analytics | null = null
let trackersStarted = false

export function readStoredTrackingConsent(): TrackingConsent | null {
  try {
    const stored = window.localStorage.getItem(TRACKING_CONSENT_STORAGE_KEY)
    return stored === 'granted' || stored === 'denied' ? stored : null
  } catch {
    return null
  }
}

export function timeZoneNeedsTrackingConsent(timeZone: string | null): boolean {
  return timeZone !== null && CONSENT_REGION_TIME_ZONES.has(timeZone)
}

export function trackingAllowed(stored: TrackingConsent | null, timeZone: string | null): boolean {
  if (stored !== null) {
    return stored === 'granted'
  }
  return !timeZoneNeedsTrackingConsent(timeZone)
}

export function getWebAnalytics(): Analytics | null {
  return analytics
}

function browserTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null
  } catch {
    return null
  }
}

function startTrackers(): void {
  if (trackersStarted) {
    // Accepted again after rejecting on this page: the scripts are already loaded.
    window.fbq?.('consent', 'grant')
    if (analytics !== null) {
      setAnalyticsCollectionEnabled(analytics, true)
    }
    return
  }
  trackersStarted = true
  // fbq('init') and the PageView were queued by index.html and are sent once fbevents.js loads.
  window.__ISSEN_LOAD_META_PIXEL__?.()
  if (buildEnv === 'docker') {
    analytics = getAnalytics(initializeApp(FIREBASE_CONFIG))
    // @ts-ignore gtag is defined by Firebase Analytics.
    window.gtag('config', 'G-FXGWW6G15V', {
      'linker': {
        'accept_incoming': true
      }
    })
  }
}

function stopTrackers(): void {
  if (!trackersStarted) {
    return
  }
  window.fbq?.('consent', 'revoke')
  if (analytics !== null) {
    setAnalyticsCollectionEnabled(analytics, false)
  }
  // The trackers set their cookies on the registrable domain (issen.com), so expire
  // them on every parent domain of this host as well as on the host itself.
  const hostParts = window.location.hostname.split('.')
  const domains = [null, ...hostParts.slice(0, -1).map((_, index) => hostParts.slice(index).join('.'))]
  for (const cookie of document.cookie.split(';')) {
    const name = cookie.split('=')[0].trim()
    if (!TRACKER_COOKIE_PATTERN.test(name)) {
      continue
    }
    for (const domain of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain === null ? '' : `; domain=${domain}`}`
    }
  }
}

// Called once at startup (not on marketing email links, which load no trackers).
export function initializeTrackingConsent(): void {
  const stored = readStoredTrackingConsent()
  if (trackingAllowed(stored, browserTimeZone())) {
    startTrackers()
  } else if (stored === null) {
    trackingConsentBannerOpen.value = true
  }
}

export function setTrackingConsent(consent: TrackingConsent): void {
  try {
    window.localStorage.setItem(TRACKING_CONSENT_STORAGE_KEY, consent)
  } catch {
    // The choice still applies to this page; the banner asks again next visit.
  }
  trackingConsentBannerOpen.value = false
  if (consent === 'granted') {
    startTrackers()
  } else {
    stopTrackers()
  }
}

export function openTrackingConsentSettings(): void {
  trackingConsentBannerOpen.value = true
}
